import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Lock, Key, Eye, Server, CheckCircle } from 'lucide-react';

const SecurityPage = () => {
  const securityFeatures = [
    {
      icon: Lock,
      title: 'End-to-End Encryption',
      description: 'All data is encrypted in transit using TLS 1.3 and at rest using AES-256 encryption.'
    },
    {
      icon: Key,
      title: 'Secure Authentication',
      description: 'JWT-based authentication with bcrypt password hashing and 24-hour session expiry.'
    },
    {
      icon: Shield,
      title: 'Data Protection',
      description: 'GDPR compliant with strict data privacy policies and user consent management.'
    },
    {
      icon: Server,
      title: 'Infrastructure Security',
      description: 'Hosted on Cloudflare\'s enterprise-grade infrastructure with DDoS protection.'
    },
    {
      icon: Eye,
      title: 'Privacy First',
      description: 'Your data is never sold or shared. You maintain full ownership of your information.'
    },
    {
      icon: CheckCircle,
      title: 'Regular Audits',
      description: 'Continuous security monitoring and regular third-party security audits.'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-white">
      <div className="max-w-6xl mx-auto px-4 py-12">
        <Link 
          to="/" 
          className="inline-flex items-center text-primary-600 hover:text-primary-700 mb-8"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-4xl font-bold text-gray-900 mb-6">Security</h1>
          <p className="text-xl text-gray-600 mb-12">
            Your security and privacy are our top priorities
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {securityFeatures.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-lg shadow-md p-6"
              >
                <feature.icon className="w-12 h-12 text-primary-600 mb-4" />
                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>

          <div className="bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-2xl font-bold mb-6">Our Security Commitments</h2>
            
            <div className="space-y-4">
              <div className="flex items-start">
                <CheckCircle className="w-6 h-6 text-green-500 mr-3 mt-0.5" />
                <div>
                  <h3 className="font-semibold mb-1">SOC 2 Type II Compliance</h3>
                  <p className="text-gray-600">We maintain strict compliance with industry standards for data security.</p>
                </div>
              </div>

              <div className="flex items-start">
                <CheckCircle className="w-6 h-6 text-green-500 mr-3 mt-0.5" />
                <div>
                  <h3 className="font-semibold mb-1">99.9% Uptime SLA</h3>
                  <p className="text-gray-600">Guaranteed availability with redundant systems and automatic failover.</p>
                </div>
              </div>

              <div className="flex items-start">
                <CheckCircle className="w-6 h-6 text-green-500 mr-3 mt-0.5" />
                <div>
                  <h3 className="font-semibold mb-1">Daily Backups</h3>
                  <p className="text-gray-600">Automatic daily backups with point-in-time recovery capabilities.</p>
                </div>
              </div>

              <div className="flex items-start">
                <CheckCircle className="w-6 h-6 text-green-500 mr-3 mt-0.5" />
                <div>
                  <h3 className="font-semibold mb-1">Zero-Knowledge Architecture</h3>
                  <p className="text-gray-600">We cannot access your encrypted data without your authorization.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 bg-primary-50 rounded-lg p-6 text-center">
            <Shield className="w-12 h-12 text-primary-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">Report a Security Issue</h3>
            <p className="text-gray-600 mb-4">
              Found a vulnerability? We appreciate responsible disclosure.
            </p>
            <Link to="/contact" className="btn-primary inline-block">
              Contact Security Team
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default SecurityPage;
